<script setup lang="ts">
import type { NavigationItem } from '../../models/navigation/NavigationItems.model'
import AppIcon from '@layer/components/elements/AppIcon.vue'
import { useLocalePath } from '#i18n'
import { computed } from 'vue'

const props = defineProps<{
  item: NavigationItem
  isCollapsed: boolean
  isActive: boolean
  isExpanded: boolean
  isMobile: boolean
}>()

const emit = defineEmits<{
  clickItem: [item: NavigationItem, event: MouseEvent]
}>()

const localePath = useLocalePath()

const resolvedPath = computed(() => {
  if (!props.item.to) return undefined
  const path = localePath(props.item.to)
  return path
})

const handleClick = (event: MouseEvent) => {
  emit('clickItem', props.item, event)
}
</script>

<template>
  <div
    :class="[
      'group mb-2 flex min-h-10 cursor-pointer items-center rounded-lg text-black',
      isCollapsed ? 'justify-center p-2' : 'px-3 py-2.5',
      'focus-within:bg-primary-50 hover:bg-primary-50',
      { 'bg-primary-50': isActive },
      { 'bg-primary-50': isExpanded && item.children },
    ]"
    data-testid="nav-item"
    role="menuitem"
    @click="handleClick"
  >
    <NuxtLink
      v-if="item.to"
      :to="resolvedPath"
      class="flex flex-grow items-center outline-none focus:outline-none"
      :class="[isCollapsed ? 'justify-center' : '', { 'font-bold': isActive }]"
      data-testid="nav-link"
      :aria-expanded="
        item.children ? (isExpanded ? 'true' : 'false') : undefined
      "
      :aria-current="isActive ? 'page' : undefined"
      tabindex="-1"
    >
      <div
        :class="[
          'flex items-center',
          isCollapsed ? 'justify-center' : 'flex-grow',
          'transition-all duration-200',
          {
            'group-hover:font-bold': !isCollapsed,
            'group-focus-within:font-bold': !isCollapsed,
          },
        ]"
      >
        <AppIcon
          :name="item.icon"
          size="sm"
          color="basics-icon"
          :class="[
            isCollapsed ? 'm-0' : 'mr-3',
            'transition-colors duration-200',
          ]"
          aria-hidden="true"
        />
        <span v-if="!isCollapsed" data-testid="nav-item-label">{{
          item.label
        }}</span>
      </div>
    </NuxtLink>

    <div
      v-else
      class="flex flex-grow items-center outline-none focus:outline-none"
      :class="[
        isCollapsed ? 'justify-center' : '',
        { 'pointer-events-none': item.children && !isCollapsed && !isMobile },
        { 'font-bold': isActive },
      ]"
      data-testid="nav-div"
      :aria-expanded="
        item.children ? (isExpanded ? 'true' : 'false') : undefined
      "
    >
      <div
        :class="[
          'flex items-center',
          isCollapsed ? 'justify-center' : 'flex-grow',
          'transition-all duration-200',
          {
            'group-hover:font-bold': !isCollapsed,
            'group-focus-within:font-bold': !isCollapsed,
          },
        ]"
      >
        <AppIcon
          :name="item.icon"
          size="sm"
          color="basics-icon"
          :class="[
            isCollapsed ? 'm-0' : 'mr-3',
            'transition-colors duration-200',
          ]"
          aria-hidden="true"
        />
        <span v-if="!isCollapsed" data-testid="nav-item-label">{{
          item.label
        }}</span>
      </div>
    </div>

    <AppIcon
      v-if="!isCollapsed && item.children"
      :name="isExpanded ? 'fa6-solid:angle-down' : 'fa6-solid:angle-right'"
      size="xs"
      color="basics-icon"
      class="ml-auto transform transition-transform duration-200"
      aria-hidden="true"
      :class="{
        'pointer-events-none': item.children && !isCollapsed && !isMobile,
      }"
    />
  </div>
</template>

<style scoped>
div[role='menuitem']:focus-visible {
  outline: none;
  position: relative;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.7);
  z-index: 1;
}
</style>
